import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router";
import CloseIcon from '@mui/icons-material/Close';

type ContactData = {
  name: string;
  designation: string;
  department: string;
  email: string;
  mobile: string;
  singlePointOfContact: string;
};

const emptyContact: ContactData = {
  name: "",
  designation: "",
  department: "",
  email: "",
  mobile: "",
  singlePointOfContact: "no",
};
type Props = {
  contacts: ContactData[];
  setContacts: React.Dispatch<React.SetStateAction<ContactData[]>>;
  onSuccess?: () => void;
  validateContactsExternally?: (validate: () => boolean) => void;
  variant?: "page" | "embedded";
};

const ContactInfoForm: React.FC<Props> = ({
  contacts,
  setContacts,
  validateContactsExternally,
  variant = "page",
}) => {

  const navigate = useNavigate();
  const isEmbedded = variant === "embedded";

  // const [contacts, setContacts] = useState<ContactData[]>([emptyContact]);

  const [errors, setErrors] = useState<{ [index: number]: { [key: string]: string } }>({});

  const validateContact = (contact: ContactData) => {
    const errs: { [key: string]: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!contact?.name?.trim()) {
      errs.name = "Name is required";
    }
    if (contact?.email && !emailRegex.test(contact?.email)) {
      errs.email = "Invalid email address";
    }
    if (!String(contact?.mobile || '').trim()) {
      errs.mobile = "Mobile number is required";
    } else if (!phoneRegex.test(String(contact?.mobile || ''))) {
      errs.mobile = "Invalid mobile number. It should be 10 digits";
    }

    return errs;
  };

  useEffect(() => {
    if (validateContactsExternally) {
      validateContactsExternally(validateAllContacts);
    }
  }, [contacts]);

  const validateAllContacts = () => {
    let isValid = true;
    const newErrors: typeof errors = {};

    contacts.forEach((contact, index) => {
      const err = validateContact(contact);
      if (Object.keys(err).length > 0) {
        newErrors[index] = err;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };


  const handleChange = (index: number, field: keyof ContactData, value: string) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setContacts(newContacts);
    if (errors[index]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          newErrors[index][field] = "";
        }
        return newErrors;
      });
    }
  };

  const handleAddContact = () => {
    setContacts((prev) => [...prev, emptyContact]);
  };

  const handleRemoveContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      const updatedErrors: typeof newErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const keyNum = Number(key);
        updatedErrors[keyNum > index ? keyNum - 1 : keyNum] = newErrors[keyNum];
      });
      return updatedErrors;
    });
  };

  return (
    <Box sx={isEmbedded ? undefined : { maxWidth: 600, margin: "auto", mt: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
            Contact Information
          </Typography>

          {contacts.map((contact, idx) => (
            <Box key={idx} sx={{ mb: 4, px: 2, py:3, border: "1px solid #ddd", borderRadius: 1 ,boxShadow: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" mb={0}>
                  Contact {idx + 1}
                </Typography>
                {contacts.length > 1 && idx !== 0 && (
                  <IconButton
                    size="small"
                    aria-label="remove contact"
                    onClick={() => handleRemoveContact(idx)}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={
                      <Typography component="span">
                        Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    }
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={contact.name}
                    onChange={(e) => handleChange(idx, "name", e.target.value)}
                    error={!!errors[idx]?.name}
                    helperText={errors[idx]?.name}
                  // required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Designation"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={contact.designation}
                    onChange={(e) => handleChange(idx, "designation", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Department"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={contact.department}
                    onChange={(e) => handleChange(idx, "department", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={contact.email}
                    onChange={(e) => handleChange(idx, "email", e.target.value)}
                    error={!!errors[idx]?.email}
                    helperText={errors[idx]?.email}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={
                      <Typography component="span">
                        Mobile<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    }
                    // label="Mobile"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={contact.mobile}
                    onChange={(e) => handleChange(idx, "mobile", e.target.value.replace(/\D/g, ""))}
                    error={!!errors[idx]?.mobile}
                    helperText={errors[idx]?.mobile}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />

                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend">Single Point of Contact?</FormLabel>
                    <RadioGroup
                      row
                      value={contact.singlePointOfContact}
                      onChange={(e) => handleChange(idx, "singlePointOfContact", e.target.value)}
                    >
                      <FormControlLabel value="yes" control={<Radio sx={{p:0 ,ml:1 ,mr:1}} />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio sx={{p:0 ,ml:1 ,mr:1}} />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={handleAddContact} color="secondary" size="small">
              ADD
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactInfoForm;
